import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { useDeleteVolume } from '@/service/gpu-instance/volume-hook';
import type { Volume } from '@/service/gpu-instance/volume-type';
import { toast } from 'sonner';

interface DeleteVolumeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volume: Volume | null;
  onSuccess?: () => void;
}

export const DeleteVolumeDialog = ({
  open,
  onOpenChange,
  volume,
  onSuccess,
}: DeleteVolumeDialogProps) => {
  const [confirmName, setConfirmName] = useState('');
  const deleteVolume = useDeleteVolume();

  if (!volume) return null;

  const isValid = confirmName === volume.name;

  const handleDelete = async () => {
    if (!isValid) return;

    try {
      await deleteVolume.mutateAsync(volume.id);
      toast.success('Volume deleted', {
        description: `Volume "${volume.name}" has been deleted.`,
      });
      onOpenChange(false);
      onSuccess?.();
      setConfirmName('');
    } catch {
      toast.error('Failed to delete volume');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Volume
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the volume and all its data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              All data on this volume will be permanently lost. Make sure you have backups if needed.
            </AlertDescription>
          </Alert>

          {/* Volume Info */}
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Volume Name</span>
              <span className="font-medium">{volume.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Size</span>
              <span>{volume.requestedSize} GB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Storage Class</span>
              <span>{volume.storageClass.displayName}</span>
            </div>
            {volume.snapshotCount > 0 && (
              <div className="flex justify-between text-destructive">
                <span>Snapshots</span>
                <span>{volume.snapshotCount} snapshot(s) will also be deleted</span>
              </div>
            )}
          </div>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <Label htmlFor="confirm-name">
              Type <span className="font-mono font-bold">{volume.name}</span> to confirm
            </Label>
            <Input
              id="confirm-name"
              placeholder="Enter volume name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className={confirmName && confirmName !== volume.name ? 'border-destructive' : ''}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isValid || deleteVolume.isPending}
          >
            {deleteVolume.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete Volume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteVolumeDialog;
